/**
 * Airline branding resolution — keeps the displayed airline LOGO in lockstep
 * with the airline NAME a segment claims to be.
 *
 * Why this exists: booking flight_data snapshots the marketing_carrier at
 * search time (name + iata_code + gstatic/Duffel logo URLs). Admin edits via
 * AdminEditModal historically rewrote only `marketing_carrier.name`, so a
 * renamed leg kept the OLD airline's logo_symbol_url/iata_code forever —
 * 4FLGZU's return read "Delta" next to the Southwest mark. Every surface that
 * renders an airline image resolves through here so the name (what the
 * customer is told) always wins over stale stored branding.
 *
 * Logo asset family: gstatic 70px PNGs — the same URLs SerpAPI returns and
 * duffel-flight-search.js normalizes Duffel offers to (they render cleanly on
 * the dark UI, unlike Duffel's light-background SVGs).
 */
import { resolveAirlineIata } from '../../supabase/functions/_shared/airline-iata.js';

/** The name → IATA table lives with the edge functions so checkout can use it. */
export { normalizeAirlineName, resolveAirlineIata } from '../../supabase/functions/_shared/airline-iata.js';

/** Same asset family the flight-search normalizers store on every booking. */
export function gstaticAirlineLogoUrl(iataCode) {
  const code = String(iataCode || '').trim().toUpperCase();
  if (!/^[A-Z0-9]{2,3}$/.test(code)) return '';
  return `https://www.gstatic.com/flights/airline_logos/70px/${code}.png`;
}

/**
 * Full branding block for a recognized airline name — the exact fields the
 * flight-search normalizers persist on marketing_carrier. Null when the name
 * isn't in the map (caller decides whether to keep or clear stored branding).
 */
export function resolveCarrierBranding(name) {
  const iata = resolveAirlineIata(name);
  if (!iata) return null;
  const logo = gstaticAirlineLogoUrl(iata);
  return { iata_code: iata, logo_symbol_url: logo, logo_lockup_url: logo };
}

/**
 * Display-time logo pick for a marketing_carrier-shaped object
 * ({ name, iata_code, logo_symbol_url, logo_lockup_url }).
 *
 * Name-first: when the name resolves to an IATA code that DISAGREES with the
 * stored iata_code, the stored logo belongs to a previous airline (admin
 * rename) — derive the logo from the name instead. In every other case
 * (name and stored code agree, or the name is unrecognized) the stored URL is
 * returned unchanged — fresh search results and untouched bookings render
 * byte-identical to before, and callers keep their own legacy fallbacks.
 */
export function resolveCarrierLogo(carrier) {
  if (!carrier) return null;
  const nameIata = resolveAirlineIata(carrier.name);
  const storedIata = String(carrier.iata_code || '').trim().toUpperCase();
  if (nameIata && nameIata !== storedIata) return gstaticAirlineLogoUrl(nameIata);
  return carrier.logo_symbol_url || carrier.logo_lockup_url || null;
}
